/* eslint-disable max-len */
import React, { useState, useRef, useEffect, useContext } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { autocompleteClasses } from '@mui/material/Autocomplete';
import { styled } from '@mui/material/styles';
import { Grid, Typography } from '@mui/material';
import { LanguageContext } from '../../../../../../../libs/Context';
import { useSelector } from 'react-redux';
import { generateRandomUUID, queryParams } from '../../../../../../../libs/HelperContent';
import { getEmployeesData } from '../../../../../../actions/employee_actions';
import useDebounce from '../../../../../../../components/Hooks/useDebounce';
import useInterSectionObserver from '../../../../../../../libs/CustomHooks/useIntersectionObserver';
import ProfilePic from '../../../../../../../components/Common/ProfilePic';

const Root = styled('div')(
	({ theme }) => `
    color: ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,.85)'};
    font-size: 14px;
    position: relative;
  `
);

const InputWrapper = styled('div')(
	({ theme }) => `
    width: 100%;
    border: 1px solid ${theme.palette.mode === 'dark' ? '#434343' : 'var(--whiteD9)'};
    background-color: ${theme.palette.mode === 'dark' ? '#141414' : 'var(--whiteColor)'};
    border-radius: 13px;
    padding: 1px;
    font-size: 16px;
    font-weight: 400;
    display: flex;
    flex-wrap: wrap;

    &:hover {
      border-color: ${theme.palette.mode === 'dark' ? '#177ddc' : '#000000'};
    }

    &.focused {
      border-color: ${theme.palette.mode === 'dark' ? '#177ddc' : '#40a9ff'};
      box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
    }

    & input {
      background-color: ${theme.palette.mode === 'dark' ? '#141414' : 'var(--whiteColor)'};
      color: ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,.85)'};
      height: 40px;
      box-sizing: border-box;
      padding: 4px 6px;
      width: 0;
      min-width: 30px;
      flex-grow: 1;
      border: 0;
      margin: 0;
      outline: 0;
    }
  `
);

const Listbox = styled('ul')(
	({ theme }) => `
    width: 100%;
    margin: 2px 0 0;
    padding: 0;
	top:42px;
    position: absolute;
    list-style: none;
    background-color: ${theme.palette.mode === 'dark' ? '#141414' : 'var(--whiteColor)'};
    overflow: auto;
    max-height: 250px;
    border-radius: 13px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    z-index: 222222;

    & li {
      padding: 5px var(--pixel12);
      display: flex;

      & span {
        flex-grow: 1;
      }

      & svg {
        color: transparent;
      }
    }

    & li[aria-selected='true'] {
      background-color: ${theme.palette.mode === 'dark' ? '#2b2b2b' : '#fafafa'};
      font-weight: 600;

      & svg {
        color: #1890ff;
      }
    }

    & li.${autocompleteClasses.focused} {
      background-color: ${theme.palette.mode === 'dark' ? '#003b57' : '#e6f7ff'};
      cursor: pointer;

      & svg {
        color: currentColor;
      }
    }
  `
);

const AutoCompleteSearch = (props) =>
{
	const { handleSelEmp } = props;
	const [ inputValue, setInputValue ] = useState('');
	const [ isOpen, setIsOpen ] = useState(false);
	const debSeachValue = useDebounce(inputValue, 500);
	const userData = useSelector((state:any) => state.mysession.session);
	const queryParamRes = queryParams(userData?.userId, userData?.companyId, userData?.empRole, userData?.role, 'get');
	const [ list, setList ] = useState<any>([]);
	const [ count, setCount ] = useState(0);
	const loaderRef = useRef<any>();
	const limit = 10;
	const [ page, setPage ] = useState(0);
	const inputRef = useRef<any>(null);
	const listRef = useRef<any>(null);
	const lastEleRef = useRef<HTMLDivElement | null>(null);

	const LoadMoreList = useInterSectionObserver({
		lastEleRef, loaderRef, count, data : list, setPage
	});

	const getEmployeeCount = async (query:any) =>
	{
		try
		{
			const cQuery = `${query}&count=true`;
			const res = await getEmployeesData(cQuery);

			if (res?.status)
			{
				setCount(res?.count);
			}
			else
			{
				setCount(0);
			}
		}
		catch (err)
		{
			// eslint-disable-next-line no-console
			console.error('Error fetching host count:', err);
		}
	};

	const getEmployeesList = async () =>
	{
		try
		{
			const query:any = `?search=${inputValue}&status=approve${queryParamRes}&limit=${limit}&skip=${page}`;

			const res = await getEmployeesData(query);
			const newD = page === 0 ? res?.data : [ ...list, ...(res?.data ?? []) ];

			page === 0
				? setList(res?.data)
				: setList(newD);

			page === 0 && res?.data?.length >= 10
				? getEmployeeCount(query)
				: setCount(res?.data?.length);

			loaderRef.current = false;
		}
		catch (error)
		{
			loaderRef.current = false;
		}
	};

	const handleClickOutside = (event) =>
	{
		if (
			listRef.current &&
      !listRef.current.contains(event.target) &&
      inputRef.current &&
      !inputRef.current.contains(event.target)
		)
		{
			setIsOpen(false);
		}
	};

	useEffect(() =>
	{
		document.addEventListener('mousedown', handleClickOutside);

		return () =>
		{
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	const handleInputChange = (event) =>
	{
		const value = event.target.value;

		setInputValue(value);
		// setIsOpen(Boolean(value));
	};

	const handleSuggestionClick = async (suggestion) =>
	{
		if (suggestion?.profileImage)
		{
			// const profileKey = `profile/${suggestion.userId}_profile.jpeg`;
			// const res = await copyS3File(profileKey, `${userData?.userId}/webinar/speaker/${new Date().valueOf()}.jpeg`);
			const profile = {
				// name : res?.key,
				// key  : res?.key,
				// type : 'image/jpeg',
				// url  : res.url
				name : 'speakerImage',
				key  : `public/profileImages/${suggestion.userId}.jpeg`,
				type : 'image/jpeg',
				url  : suggestion.profileImage
			};

			suggestion.profile = profile;
		}

		handleSelEmp(suggestion);
		setInputValue(suggestion.name);
		setIsOpen(false);
	};

	useEffect(() =>
	{
		LoadMoreList();
	}, [ lastEleRef, count, list, page ]);

	useEffect(() =>
	{
		getEmployeesList();
	}, [ debSeachValue, page ]);

	const { language } = useContext(LanguageContext);

	return (
		<Root className='flexMinWidthCol'>
			<InputWrapper className='flexRow justifyCntr alignCntr'>
				<SearchIcon fontSize='small' className='mrgnLSM' />
				<input
					onFocus={() => setIsOpen(true)}
					ref={inputRef}
					placeholder={language.search}
					value={inputValue}
					onChange={handleInputChange}
				/>
			</InputWrapper>
			{isOpen && (
				<Listbox ref={listRef}>
					{list.map((option, index) => (
						<>
							<li
								className='pointer'
								key={`option- ${option?._id ?? generateRandomUUID()}`}

							>
								<Grid container alignItems='center' onClick={() => handleSuggestionClick(option)}>
									<Grid item className='circle circleSM'>
										{/* <LazyLoadImage
											src={'images/icons/defaultUser.svg'}
											alt='img'
											className='brdrRadius50'
											width={32}
											height={32}
										/> */}
										<ProfilePic
											imagePath={`${option?.userId}.jpeg`}
											defaultImg={undefined}
											alt={'profile not found'}
											className={'circle circleSM'}
											from='selfProfile'
										/>
									</Grid>
									<Grid item xs pl='var(--pixel8)'>
										<Typography variant='body2' noWrap>{option.name}
											<Typography variant='caption' sx={{ textTransform: 'capitalize' }} pl='var(--pixel4)' component={'span'}>
												({option.designation})
											</Typography>
										</Typography>
										<Typography variant='caption' color='textSecondary' noWrap>
											{option.email}
										</Typography>
									</Grid>
								</Grid>
							</li>
							{index === (list?.length - 2) && <span ref={lastEleRef}/>}
						</>

					))}
				</Listbox>
			)}
		</Root>
	);
};

export default AutoCompleteSearch;
